import { forwardRef } from '@heroui/system';
import { useState } from 'react';
import { MdOutlineCancel, MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';
import { Input, InputProps } from '@heroui/react';

export interface CustomInputProps extends Omit<InputProps, 'ref'> {
  roundedClassName?: string;
  onClear?: () => void;
}

const CustomInput = forwardRef<'input', CustomInputProps>((props, ref) => {
  const {
    roundedClassName = 'rounded-lg',
    isClearable,
    startContent,
    label,
    value,
    onChange,
    onClear,
    type,
    id,
    ...restProps
  } = props;
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const inputProps = restProps as Omit<
    InputProps,
    'ref' | 'isClearable' | 'startContent' | 'label' | 'value' | 'onChange' | 'onClear' | 'type'
  >;

  const isPasswordType = type === 'password';
  const inputType = isPasswordType && isPasswordVisible ? 'text' : type;

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible((prev) => !prev);
  };

  const customClearButton = (
    <button type="button" aria-label="Clear input value" className="clear-button pointer-events-auto" onClick={onClear}>
      <MdOutlineCancel size={18} />
    </button>
  );

  const passwordToggleButton = (
    <button
      type="button"
      aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
      className="pointer-events-auto cursor-pointer text-base-5 flex items-center justify-center h-full"
      onClick={handleTogglePasswordVisibility}
    >
      {isPasswordVisible ? <MdOutlineVisibilityOff size={20} /> : <MdOutlineVisibility size={20} />}
    </button>
  );

  const getEndContent = () => {
    if (isPasswordType) {
      return passwordToggleButton;
    }
    if (isClearable) {
      return customClearButton;
    }
    return undefined;
  };

  return (
    <Input
      ref={ref}
      type={inputType}
      value={value}
      onChange={onChange}
      endContent={getEndContent()}
      classNames={{
        base: 'tmdx-input group',
        inputWrapper: [
          'bg-base-60',
          'group-data-[focus=true]:bg-base-40!',
          'group-data-[invalid=true]:bg-base-60!',
          'group-data-[invalid=true]:group-data-[hover=true]:bg-base-60!',
          'tmdx-input__wrapper',
          roundedClassName,
          'transition-none!',
          !label && 'min-h-8 h-8',
          !!label && 'hasLabel',
          'shadow-none',
        ],
        input: [
          'text-base-5',
          'placeholder:text-base-30',
          'text-m-r!',
          'group-data-[invalid=true]:text-base-5!',
          'tmdx-input__item',
        ],
        label: [
          'text-base-20',
          'text-s-r!',
          'tmdx-input__label',
          'group-focus-within:text-base-10',
          'group-data-[invalid=true]:text-danger!',
        ],
        description: ['text-base-20', 'text-s-r', 'group-data-[invalid=true]:text-danger', 'form-item__description'],
        innerWrapper: ['bg-transparent'],
        clearButton: ['opacity-100!'],
      }}
      label={label}
      startContent={startContent}
      onWheel={(e) => (e.target as HTMLInputElement).blur()}
      spellCheck="false"
      {...inputProps}
    />
  );
});

CustomInput.displayName = 'CustomInput';

export default CustomInput;
